import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Question } from '@phosphor-icons/react'

interface NumberInputFieldProps {
  id: string
  label: string
  tooltip?: string
  prefix?: string
  suffix?: string
  value: number | string
  onChange: (v: number) => void
  placeholder?: string
  step?: number
  min?: number
  stageNumber?: number
}

export function NumberInputField({
  id,
  label,
  tooltip,
  prefix,
  suffix,
  value,
  onChange,
  placeholder = '0',
  step = 1,
  min = 0,
  stageNumber,
}: NumberInputFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        {stageNumber !== undefined && (
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center tabular-nums">
            {stageNumber}
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <label
            htmlFor={id}
            className={cn(
              'text-xs font-medium text-muted-foreground',
              stageNumber !== undefined && 'uppercase tracking-[0.06em]',
            )}
          >
            {label}
          </label>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Question weight="regular" className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="bg-card border-border text-xs max-w-[200px]">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="relative flex items-center">
        {prefix && (
          <span className="absolute left-3 text-xs text-muted-foreground select-none pointer-events-none z-10">
            {prefix}
          </span>
        )}
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          placeholder={placeholder}
          value={value}
          step={step}
          min={min}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className={cn(
            'h-10 text-sm tabular-nums font-mono bg-input border-border',
            'focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-0',
            prefix && 'pl-7',
            suffix && 'pr-8',
          )}
        />
        {suffix && (
          <span className="absolute right-3 text-xs text-muted-foreground select-none pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  )
}
